import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import api, { fmtDate } from "@/lib/api";
import { PageHero } from "@/components/common";
import { Input } from "@/components/ui/input";
import { Star, Search, Clock, BarChart3, ChevronRight, Lock } from "lucide-react";
import { toast } from "sonner";

const Tile = ({ t, onOpen, onFav }) => (
  <div className="group bg-white border border-slate-200 rounded-xl p-4 shadow-sm hover:shadow-md hover:border-[#2EA3FD]/50 transition-shadow flex flex-col" data-testid={`report-tile-${t.key}`}>
    <div className="flex items-start justify-between gap-2">
      <span className="h-9 w-9 shrink-0 rounded-lg bg-[#EFF8FF] text-[#2EA3FD] grid place-items-center"><BarChart3 size={16} /></span>
      <button onClick={() => onFav(t)} data-testid={`report-fav-${t.key}`} aria-label={`${t.favorite ? "Remove" : "Add"} favourite: ${t.name}`} className="text-slate-300 hover:text-amber-400">
        <Star size={16} className={t.favorite ? "fill-amber-400 text-amber-400" : ""} />
      </button>
    </div>
    <p className="font-semibold text-[#0B1A2F] text-sm mt-3 flex items-center gap-1">{t.name}{t.sensitive && <Lock size={11} className="text-rose-400" />}</p>
    <p className="text-xs text-slate-500 mt-1 flex-1">{t.desc}</p>
    <div className="flex items-center justify-between mt-3">
      <span className="text-[11px] text-slate-400">
        {t.count != null ? <span className="font-semibold text-[#36776B]">{t.count}</span> : null}
        {t.last_run ? <span className="ml-2">Last run {fmtDate(t.last_run)}</span> : (t.count == null ? (t.available ? "Ready" : "Coming soon") : "")}
      </span>
      <button onClick={() => onOpen(t)} data-testid={`report-open-${t.key}`} className="text-xs font-semibold text-[#2EA3FD] hover:underline flex items-center gap-0.5">Open report<ChevronRight size={13} /></button>
    </div>
  </div>
);

export default function ReportHub() {
  const nav = useNavigate();
  const [d, setD] = useState(null);
  const [q, setQ] = useState("");
  const [group, setGroup] = useState("all");
  const load = () => api.get("/report-hub/catalog").then((r) => setD(r.data)).catch(() => setD(null));
  useEffect(() => { load(); }, []);

  const open = (t) => nav(`/reports/${t.key}`);
  const fav = async (t) => {
    try { await api.put(`/report-hub/favorites/${t.key}`, { favorite: !t.favorite }); load(); }
    catch { toast.error("Couldn't update favourite"); }
  };

  const filtered = useMemo(() => {
    if (!d) return [];
    const s = q.trim().toLowerCase();
    return d.tiles.filter((t) => (group === "all" || t.group === group) && (!s || t.name.toLowerCase().includes(s) || t.desc.toLowerCase().includes(s)));
  }, [d, q, group]);

  if (!d) return <div className="text-slate-400" data-testid="report-hub-loading">Loading reports…</div>;
  const byKey = Object.fromEntries(d.tiles.map((t) => [t.key, t]));
  const favTiles = d.favorites.map((k) => byKey[k]).filter(Boolean);
  const recentTiles = d.recents.map((k) => byKey[k]).filter(Boolean);
  const groupsWithTiles = d.groups.filter((g) => filtered.some((t) => t.group === g));

  return (
    <div className="space-y-6" data-testid="report-hub">
      <PageHero title="Reports" subtitle="View, filter and export HR information across CASS." icon={BarChart3} eyebrow="Reporting" pageKey="reports" />

      <div className="flex flex-col sm:flex-row gap-3">
        <div className="relative flex-1">
          <Search size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
          <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search reports…" className="pl-9" data-testid="report-search" aria-label="Search reports" />
        </div>
        <select value={group} onChange={(e) => setGroup(e.target.value)} aria-label="Filter reports by group" className="border border-slate-200 rounded-md h-10 px-3 text-sm bg-white" data-testid="report-group-filter">
          <option value="all">All groups</option>
          {d.groups.map((g) => <option key={g} value={g}>{g}</option>)}
        </select>
      </div>

      {!q && group === "all" && favTiles.length > 0 && (
        <section data-testid="report-favorites">
          <h2 className="text-sm font-semibold text-[#0B1A2F] flex items-center gap-2 mb-3"><Star size={15} className="fill-amber-400 text-amber-400" />Favourites</h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">{favTiles.map((t) => <Tile key={t.key} t={t} onOpen={open} onFav={fav} />)}</div>
        </section>
      )}

      {!q && group === "all" && recentTiles.length > 0 && (
        <section data-testid="report-recents">
          <h2 className="text-sm font-semibold text-[#0B1A2F] flex items-center gap-2 mb-3"><Clock size={15} className="text-[#2EA3FD]" />Recently used</h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">{recentTiles.map((t) => <Tile key={t.key} t={t} onOpen={open} onFav={fav} />)}</div>
        </section>
      )}

      {groupsWithTiles.length === 0 && <p className="text-sm text-slate-400" data-testid="report-empty">No reports match your search.</p>}
      {groupsWithTiles.map((g) => (
        <section key={g} data-testid={`report-group-${g.replace(/[^a-z0-9]+/gi, "-").toLowerCase()}`}>
          <h2 className="text-sm font-semibold text-[#0B1A2F] mb-3 border-l-4 border-[#2EA3FD] pl-2">{g}</h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
            {filtered.filter((t) => t.group === g).map((t) => <Tile key={t.key} t={t} onOpen={open} onFav={fav} />)}
          </div>
        </section>
      ))}
    </div>
  );
}
